/**
 * "Send test payload" — universal trigger for any inbound workflow.
 *
 * Replaces WebhookTriggerDialog (which only worked for webhook
 * ingress). This one handles every ingress kind uniformly because the
 * backend goes through the production intake path (dispatch → messages
 * row pending → intake worker → workflow start), not the webhook HTTP
 * receive.
 *
 * Behaviour:
 *   - 0 bound ingress sources → empty state with link to /ingress
 *   - 1 bound source         → no picker, just the payload editor
 *   - N bound sources        → dropdown to choose which to send through
 *
 * The "produces a real workflow execution" promise means the user can
 * follow the link to /workflow-runs to see the run after a few
 * seconds (the intake worker runs every 3s).
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { ExternalLink, Loader2, Send } from "lucide-react";
import * as React from "react";
import { Link, useNavigate } from "react-router-dom";

import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { workflowsApi } from "@/lib/api-workflows";


export interface SendTestPayloadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workflowId: string;
  /** Optional initial payload (e.g. from the saved sample). */
  initialPayload?: unknown;
}


export function SendTestPayloadDialog({
  open,
  onOpenChange,
  workflowId,
  initialPayload,
}: SendTestPayloadDialogProps) {
  const nav = useNavigate();

  // Pull the bound ingress sources for this workflow's active version.
  // Refetched on every open so a change to the workflow (saved a new
  // version with different sources) is picked up immediately.
  const bindingsQ = useQuery({
    queryKey: ["workflow", workflowId, "ingress-bindings"],
    queryFn: () => workflowsApi.ingressBindings(workflowId),
    enabled: open,
    staleTime: 5_000,
  });
  const bindings = bindingsQ.data ?? [];

  const [selectedSourceId, setSelectedSourceId] = React.useState<string | null>(null);
  const [payloadText, setPayloadText] = React.useState<string>("{\n  \n}");
  const [parseError, setParseError] = React.useState<string | null>(null);

  // Seed defaults when the dialog opens or bindings change.
  React.useEffect(() => {
    if (!open) return;
    setParseError(null);
    // Pre-select the single source when there's exactly one.
    if (bindings.length === 1) {
      setSelectedSourceId(bindings[0].ingress_source_id);
    } else if (bindings.length === 0) {
      setSelectedSourceId(null);
    }
    // Pre-populate payload from initialPayload if provided.
    if (initialPayload !== undefined) {
      setPayloadText(JSON.stringify(initialPayload, null, 2));
    }
  }, [open, bindings, initialPayload]);

  const send = useMutation({
    mutationFn: async () => {
      let payload: unknown;
      try {
        payload = JSON.parse(payloadText);
      } catch (e) {
        const msg = e instanceof Error ? e.message : "invalid JSON";
        setParseError(msg);
        throw new Error(msg);
      }
      setParseError(null);
      return workflowsApi.sendTestPayload(workflowId, {
        ingress_source_id: selectedSourceId!,
        payload,
      });
    },
  });

  const response = send.data;
  const canSend =
    selectedSourceId !== null &&
    payloadText.trim().length > 0 &&
    !send.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-sm font-semibold">
            Send test payload
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-3">
          {bindingsQ.isLoading ? (
            <div className="flex items-center justify-center py-6 text-xs text-muted-foreground">
              <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />
              Loading bound ingress sources…
            </div>
          ) : bindings.length === 0 ? (
            // No source nodes resolve to an ingress source. Could be a
            // pure outbound workflow, or someone removed the source
            // node from the active version. Either way, no point in
            // letting the user send.
            <Alert tone="warning" title="No ingress sources bound">
              The active version of this workflow doesn't reference any
              ingress source. Add a source node (webhook / sftp_pull /
              file_watch / db_poll / http_cron_pull) bound to a source
              from <Link to="/ingress" className="text-accent hover:underline">/ingress</Link>{" "}
              first, save & activate, then come back.
            </Alert>
          ) : (
            <>
              {/* Source picker — auto-selected when N=1, dropdown when N>1. */}
              <div className="space-y-1.5">
                <Label htmlFor="stp-source">Send through</Label>
                {bindings.length === 1 ? (
                  <div className="flex items-center gap-2 rounded-md border border-border/60 bg-muted/30 px-2.5 py-1.5 text-xs">
                    <Badge tone="neutral">{bindings[0].ingress_source_type}</Badge>
                    <span className="font-mono">{bindings[0].ingress_source_name}</span>
                    <span className="text-muted-foreground">
                      · node <span className="font-mono">{bindings[0].node_id}</span>
                    </span>
                  </div>
                ) : (
                  <select
                    id="stp-source"
                    value={selectedSourceId ?? ""}
                    onChange={(e) => setSelectedSourceId(e.target.value || null)}
                    className="h-9 w-full rounded-md border border-border bg-background px-2 text-xs"
                  >
                    <option value="">Choose an ingress source…</option>
                    {bindings.map((b) => (
                      <option key={b.node_id} value={b.ingress_source_id}>
                        {b.ingress_source_name} ({b.ingress_source_type})
                        {" — via node "}
                        {b.node_id}
                      </option>
                    ))}
                  </select>
                )}
                <p className="text-[11px] text-muted-foreground">
                  Payload travels the real production path — message row
                  written, intake worker picks it up, workflow starts
                  durably. Watch{" "}
                  <Link to="/workflow-runs" className="text-accent hover:underline">
                    Workflow Runs
                  </Link>{" "}
                  for the new execution.
                </p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="stp-payload">Payload (JSON)</Label>
                <textarea
                  id="stp-payload"
                  rows={12}
                  value={payloadText}
                  onChange={(e) => setPayloadText(e.target.value)}
                  className="block w-full resize-y border border-border bg-background px-2 py-1.5 font-mono text-xs"
                  spellCheck={false}
                />
                {parseError && (
                  <p className="text-xs text-destructive">
                    Invalid JSON: {parseError}
                  </p>
                )}
              </div>

              {send.isError && !parseError && (
                <Alert tone="danger">
                  {(send.error as Error)?.message ?? "Send failed"}
                </Alert>
              )}

              {response && (
                <Alert tone="info" title="Payload accepted">
                  <div className="space-y-1 text-xs">
                    <div>
                      message_id:{" "}
                      <span className="font-mono">
                        {response.message_id.slice(0, 8)}…
                      </span>
                      {response.deduplicated && (
                        <span className="ml-2 text-muted-foreground">
                          (deduplicated — same payload was already seen)
                        </span>
                      )}
                    </div>
                    <div>
                      The intake worker drains pending messages every ~3 s.
                      Click below to watch the execution appear:
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        onOpenChange(false);
                        nav("/workflow-runs");
                      }}
                      className="inline-flex items-center gap-1 text-accent hover:underline"
                    >
                      Open Workflow Runs
                      <ExternalLink className="h-3 w-3" strokeWidth={1.5} />
                    </button>
                  </div>
                </Alert>
              )}
            </>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          {bindings.length > 0 && (
            <Button
              size="sm"
              disabled={!canSend}
              onClick={() => send.mutate()}
            >
              {send.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />
              ) : (
                <Send className="h-3.5 w-3.5" strokeWidth={1.5} />
              )}
              Send payload
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
